import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { hasProdLogsEnabled } from './utils/helpers';

/**
 * Redux Store: Web-App-Auth
 **/

export const initialState = {
  id: 'web-app-auth',
  c1Accounts: {
    requestAccounts: false,
    c1AccountID: '',
    c1RoleID: '',
    c1AccountsList: [],
    lastAccessedAccountID: '',
    fetchRecInvitations: false,
    invitationsReceived: [],
    urlHasInvitationID: false,
  },
  c1Services: { fetchServices: false, c1ServicesList: [] },
  c1Token: { token: '', expires: '', hasError: false, fetchC1Token: false },
  c1UserDetails: {
    name: '',
    email: '',
    timezone: '',
    wsAdminID: null,
    type: '',
    hasError: false,
    hasMFAResetCode: null,
  },
  c1Common: {
    wsLoggedIn: false, // WS session state during dual sign-in phase
    wsNavigationRestricted: false, // WS session state during rescue login sign-in phase
    loggedIn: false,
    loggingOut: false,
    authDetails: { authType: '', expires: '' }, // authentication details
    hasError: false,
    appSelection: '',
    locale: 'en',
    errorMsg: '',
    isLoading: false,
    isUnauthLoading: false, // TODO: remove once V1 -> V1 migration is done
    theme: { colorMode: 'light', overrides: {} },
    showSupportModal: false,
  },
  c1MaintenanceMode: {
    enabled: false,
    bannerMessage: {},
    servicesRestrictions: {},
  },
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState.c1Common,
          storeId: 'web-app-auth',
        },
      };
    case 'HYDRATE_STORE':
      return {
        ...state,
        ...action.storeState,
      };
    case 'NAVIGATE_APP':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          appSelection: action.appName,
        },
      };
    case 'WS_LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: true,
        },
      };
    case 'WS_RESCUE_LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: true,
          wsNavigationRestricted: true,
        },
      };
    case 'WS_LOGGED_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: false,
          wsNavigationRestricted: false,
        },
      };
    case 'LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: true,
          loggingOut: false,
        },
      };
    case 'LOGGING_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: false,
          loggingOut: true,
        },
      };
    case 'ADD_AUTH_DETAILS':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          authDetails: {
            ...state.authDetails,
            ...action.authDetails,
          },
        },
      };
    case 'SET_LOCALE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...state.c1Token,
          ...action.c1Token,
        },
      };
    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
    case 'ADD_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: action.c1AccountID,
          lastAccessedAccountID: action.c1AccountID,
          c1RoleID: action.c1RoleID,
        },
      };
    case 'STORE_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: action.storeAccounts,
          requestAccounts: false,
        },
      };
    case 'STORE_RECEIVED_INVITATIONS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          hasError: false,
          invitationsReceived: action.invitationsReceived,
        },
      };
    case 'URL_HAS_INVITATION_ID': {
      return {
        ...state,
        urlHasInvitationID: action.urlHasInvitationID,
      };
    }
    case 'TOGGLE_DISPLAY_SUPPORT_MODAL':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          showSupportModal: !state.c1Common.showSupportModal,
        },
      };
    case 'SET_THEME':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case 'SET_COLOR_MODE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            overrides: action.overrides,
          },
        },
      };
    case 'ADD_USER_TYPE':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          type: action.userType,
        },
      };
    case 'ADD_USER_INFO': {
      const { name = '', email = '', timezone = '' } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          email,
          name,
          timezone,
        },
      };
    }
    case 'TOGGLE_HAS_MFA_RESET_CODE': {
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          hasMFAResetCode: action.hasMFAResetCode,
        },
      };
    }
    case 'SHOW_LOADING_INDICATOR':
    case 'HIDE_LOADING_INDICATOR': {
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          isLoading: action.loading,
        },
      };
    }
    case 'SHOW_UNAUTH_STATE_LOADING_INDICATOR':
    case 'HIDE_UNAUTH_STATE_LOADING_INDICATOR': {
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          isUnauthLoading: action.loading,
        },
      };
    }
    case 'MAINTENANCE_MODE_CONFIG_UPDATED':
      return {
        ...state,
        c1MaintenanceMode: {
          ...state.c1MaintenanceMode,
          ...action.maintenanceMode,
        },
      };
    case 'RESET_STORE':
      return {
        ...state,
        ...initialState,
        c1Common: {
          ...initialState.c1Common,
          locale: state.c1Common.locale,
          /** TODO: remove this once V1->V2 migration is done
           * show loader over login page for automatic logout from V1
           * after migration invitation is sent
           * */
          isUnauthLoading: state.c1Common.isUnauthLoading,
        },
        c1MaintenanceMode: {
          // NOTE: Do not reset the maintenance mode state
          ...state.c1MaintenanceMode,
        },
      };
    default:
      return state;
  }
}

export const getCommonType = (state = {}, key = '') => {
  const c1CommonState = state?.c1Common;
  if (c1CommonState[key] !== undefined) {
    return c1CommonState[key];
  } else {
    for (const objProp in c1CommonState) {
      if (c1CommonState[objProp] && c1CommonState[objProp][key]) {
        return c1CommonState[objProp][key];
      }
    }
  }
};

export const getMaintenanceModeState = (state, language) => {
  const { c1MaintenanceMode = {} } = state;
  return {
    enabled: c1MaintenanceMode.enabled,
    bannerMessage: language
      ? c1MaintenanceMode?.bannerMessage?.[language]
      : c1MaintenanceMode.bannerMessage,
    servicesRestrictions: c1MaintenanceMode.servicesRestrictions,
  };
};

export const getAccountDetails = (state, type) => state?.c1Accounts?.[type];
export const getAccountInfo = (state, key) => {
  const currentAccountID = getAccounts(state, 'c1AccountID');
  const accountInfo = state?.c1Accounts?.c1AccountsList.find(
    (account) => account.id === currentAccountID
  );
  return accountInfo ? accountInfo[key] : '';
};
export const getAccounts = (state, type) => state?.c1Accounts[type];
export const getC1Token = (state, type) => state?.c1Token?.[type];
export const getC1UserDetails = (state, type) => state?.c1UserDetails?.[type];

/**
 * Custom Logger Middleware
 * @param store
 * @returns {function(*): function(*=): *}
 */
/**
 * Logs all actions and states after they are dispatched.
 */
const logger = (store) => (next) => (action) => {
  if (
    (DEPLOYMENT_TARGET !== 'prod' && DEPLOYMENT_TARGET !== 'govcloud-prod') ||
    hasProdLogsEnabled
  ) {
    console.group(action.type, 'web-app-auth');
    console.debug('web-app-auth dispatching', action);

    // Call the next dispatch method in the middleware chain
    const result = next(action);

    console.debug('web-app-auth next state after dispatch', store.getState());
    console.groupEnd();
    return result;
  }
  return next(action);
};

const composeEnhancers = composeWithDevTools({ name: 'web-app-auth', trace: true, traceLimit: 25 });
export const storeInstance = createStore(reducer, composeEnhancers(applyMiddleware(logger)));
